import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Globe, 
  Palette, 
  Upload, 
  CheckCircle, 
  AlertTriangle, 
  Copy, 
  Trash2,
  Plus,
  Save,
  Loader2,
  Shield,
  Settings,
  Clock,
  X,
  Eye
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SubdomainRequestForm } from '@/components/subdomain/SubdomainRequestForm';
import { useModuleAccess } from '@/hooks/useModuleAccess';

interface WhitelabelSettings {
  id?: string;
  brand_name?: string;
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  custom_css?: string;
  hide_internal_branding?: boolean;
  custom_email_domain?: string;
  is_active?: boolean;
  is_enabled?: boolean;
}

interface CustomDomain {
  id: string;
  domain: string;
  subdomain?: string;
  full_domain: string;
  status: 'pending' | 'verifying' | 'active' | 'error' | 'disabled';
  ssl_status: 'pending' | 'provisioning' | 'active' | 'error' | 'expired';
  verification_token?: string;
  dns_records?: Array<{
    type: string;
    name: string;
    value: string;
    status: 'verified' | 'pending' | 'error';
  }>;
  verified_at?: string;
}

interface DomainMapping {
  id: string;
  domain_pattern: string;
  mapping_type: 'whitelabel' | 'redirect' | 'proxy' | 'workspace';
  features?: Record<string, any>;
  enabled_features?: string[];
  disabled_features?: string[];
  is_active: boolean;
  priority: number;
}

interface SubdomainRequest {
  id: string;
  requested_subdomain: string;
  requested_domain: string;
  full_requested_domain: string;
  requested_brand_name?: string;
  requested_primary_color?: string;
  requested_secondary_color?: string;
  requested_accent_color?: string;
  requested_logo_url?: string;
  requested_favicon_url?: string;
  additional_notes?: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'cancelled';
  requested_by: string;
  reviewed_by?: string;
  reviewed_at?: string;
  rejection_reason?: string;
  admin_notes?: string;
  created_at: string;
}

export function WorkspaceWhitelabelManager() {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;
  const moduleAccess = useModuleAccess('workspace', workspaceId || '');
  const isAdmin = moduleAccess.isOwner || moduleAccess.hasPermission('workspace_admin', 'admin');

  const [whitelabelSettings, setWhitelabelSettings] = useState<WhitelabelSettings>({
    brand_name: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#3b82f6',
    secondary_color: '#10b981',
    accent_color: '#8b5cf6',
    custom_css: '',
    hide_internal_branding: false,
    custom_email_domain: '',
    is_active: false,
    is_enabled: false,
  });

  const [newDomain, setNewDomain] = useState('');
  const [newSubdomain, setNewSubdomain] = useState('');

  // Fetch whitelabel settings
  const { data: existingWhitelabel, isLoading: loadingWhitelabel } = useQuery({
    queryKey: ['workspace-whitelabel', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return null;
      const { data, error } = await supabase
        .from('workspace_whitelabel_settings')
        .select('*')
        .eq('workspace_id', workspaceId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!workspaceId,
  });

  // Fetch custom domains
  const { data: domains = [], isLoading: loadingDomains } = useQuery({
    queryKey: ['workspace-custom-domains', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from('workspace_custom_domains')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as CustomDomain[];
    },
    enabled: !!workspaceId,
  });

  // Fetch domain mappings
  const { data: mappings = [], isLoading: loadingMappings } = useQuery({
    queryKey: ['workspace-domain-mappings', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from('workspace_domain_mappings')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('priority', { ascending: false });
      
      if (error) throw error;
      return data as DomainMapping[];
    },
    enabled: !!workspaceId,
  });

  // Fetch subdomain requests
  const { data: subdomainRequests = [], isLoading: loadingRequests } = useQuery({
    queryKey: ['subdomain-requests', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from('subdomain_requests')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SubdomainRequest[];
    },
    enabled: !!workspaceId,
  });

  const pendingRequests = subdomainRequests.filter(r => r.status === 'pending' || r.status === 'under_review');

  // Update whitelabel settings when loaded
  useEffect(() => {
    if (existingWhitelabel) {
      setWhitelabelSettings({
        ...whitelabelSettings,
        ...existingWhitelabel,
      });
    }
  }, [existingWhitelabel]);

  // Save whitelabel settings mutation
  const saveWhitelabelMutation = useMutation({
    mutationFn: async (settings: WhitelabelSettings) => {
      if (!workspaceId) throw new Error('No workspace selected');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const payload = {
        workspace_id: workspaceId,
        ...settings,
        updated_by: user.id,
      };

      const { data, error } = await supabase
        .from('workspace_whitelabel_settings')
        .upsert(payload, { onConflict: 'workspace_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-whitelabel', workspaceId] });
      toast({
        title: 'Whitelabel settings saved',
        description: 'Your whitelabel configuration has been saved successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error saving settings',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Add domain mutation
  const addDomainMutation = useMutation({
    mutationFn: async ({ domain, subdomain }: { domain: string; subdomain?: string }) => {
      if (!workspaceId) throw new Error('No workspace selected');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fullDomain = subdomain ? `${subdomain}.${domain}` : domain;
      const verificationToken = `verify-${Math.random().toString(36).substring(2, 15)}`;

      // Generate DNS records for verification
      const dnsRecords = [
        {
          type: 'CNAME',
          name: subdomain || '@',
          value: 'billportal.app',
          status: 'pending' as const,
        },
        {
          type: 'TXT',
          name: `_verification.${subdomain || ''}`.replace(/^_verification\.$/, '_verification'),
          value: verificationToken,
          status: 'pending' as const,
        },
      ];

      const { data, error } = await supabase
        .from('workspace_custom_domains')
        .insert({
          workspace_id: workspaceId,
          domain,
          subdomain: subdomain || null,
          full_domain: fullDomain,
          status: 'pending',
          verification_token: verificationToken,
          verification_method: 'dns',
          dns_records: dnsRecords,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-custom-domains', workspaceId] });
      setNewDomain('');
      setNewSubdomain('');
      toast({
        title: 'Domain added',
        description: 'Domain has been added. Please configure DNS records to verify.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error adding domain',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Activate whitelabel mutation
  const activateWhitelabelMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('No workspace selected');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First ensure settings exist
      const { data: existing } = await supabase
        .from('workspace_whitelabel_settings')
        .select('id')
        .eq('workspace_id', workspaceId)
        .maybeSingle();

      if (!existing) {
        throw new Error('Please configure whitelabel settings first');
      }

      const { data, error } = await supabase
        .from('workspace_whitelabel_settings')
        .update({
          is_active: true,
          is_enabled: true,
          updated_by: user.id,
        })
        .eq('workspace_id', workspaceId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-whitelabel', workspaceId] });
      setWhitelabelSettings(prev => ({ ...prev, is_active: true, is_enabled: true }));
      toast({
        title: 'Whitelabel activated',
        description: 'Whitelabel services are now active for this workspace.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error activating whitelabel',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSaveWhitelabel = () => {
    saveWhitelabelMutation.mutate(whitelabelSettings);
  };

  const handleAddDomain = () => {
    if (!newDomain.trim()) {
      toast({
        title: 'Domain required',
        description: 'Please enter a domain name',
        variant: 'destructive',
      });
      return;
    }
    addDomainMutation.mutate({ domain: newDomain.trim(), subdomain: newSubdomain.trim() || undefined });
  };

  const handleActivate = () => {
    if (!whitelabelSettings.brand_name) {
      toast({
        title: 'Configuration required',
        description: 'Please configure at least a brand name before activating.',
        variant: 'destructive',
      });
      return;
    }
    activateWhitelabelMutation.mutate();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'DNS record copied to clipboard',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'verified':
        return 'default';
      case 'pending':
      case 'verifying':
      case 'provisioning':
        return 'secondary';
      case 'error':
      case 'expired':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  if (!workspaceId) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Please select a workspace to manage whitelabel settings.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Whitelabel & Domain Management</h1>
          <p className="text-muted-foreground">
            Configure custom branding and domain mappings for your workspace
          </p>
        </div>
        {whitelabelSettings.is_active && whitelabelSettings.is_enabled && (
          <Badge variant="default" className="gap-2">
            <CheckCircle className="h-3 w-3" />
            Active
          </Badge>
        )}
      </div>

      <Tabs defaultValue="requests" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="requests">
            <Clock className="h-4 w-4 mr-2" />
            Requests
            {pendingRequests.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                {pendingRequests.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="whitelabel">
            <Palette className="h-4 w-4 mr-2" />
            Whitelabel Settings
          </TabsTrigger>
          <TabsTrigger value="domains">
            <Globe className="h-4 w-4 mr-2" />
            Custom Domains
          </TabsTrigger>
          <TabsTrigger value="mappings">
            <Settings className="h-4 w-4 mr-2" />
            Domain Mappings
          </TabsTrigger>
        </TabsList>

        {/* Subdomain Requests Tab */}
        <TabsContent value="requests" className="space-y-6">
          {!isAdmin ? (
            // User view: Show request form
            <SubdomainRequestForm 
              onSuccess={() => queryClient.invalidateQueries({ queryKey: ['subdomain-requests', workspaceId] })}
            />
          ) : (
            // Admin view: Show pending requests for approval
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Pending Subdomain Requests
                  </CardTitle>
                  <CardDescription>
                    Review and approve subdomain requests from workspace members
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingRequests ? (
                    <div className="text-center py-8 text-muted-foreground">Loading requests...</div>
                  ) : pendingRequests.length > 0 ? (
                    <div className="space-y-4">
                      {pendingRequests.map((request) => (
                        <SubdomainRequestCard 
                          key={request.id} 
                          request={request} 
                          onApprove={() => queryClient.invalidateQueries({ queryKey: ['subdomain-requests', workspaceId] })}
                          onReject={() => queryClient.invalidateQueries({ queryKey: ['subdomain-requests', workspaceId] })}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No pending requests. Users can submit subdomain requests from their view.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Show all requests (approved, rejected, etc.) */}
              {subdomainRequests.length > pendingRequests.length && (
                <Card>
                  <CardHeader>
                    <CardTitle>All Requests</CardTitle>
                    <CardDescription>Complete history of subdomain requests</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {subdomainRequests.filter(r => r.status !== 'pending' && r.status !== 'under_review').map((request) => (
                        <SubdomainRequestCard 
                          key={request.id} 
                          request={request} 
                          onApprove={() => {}}
                          onReject={() => {}}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Whitelabel Settings Tab */}
        <TabsContent value="whitelabel" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Brand Configuration</CardTitle>
              <CardDescription>
                Customize the appearance and branding for your workspace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brandName">Brand Name</Label>
                  <Input
                    id="brandName"
                    value={whitelabelSettings.brand_name || ''}
                    onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, brand_name: e.target.value }))}
                    placeholder="Your Company Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customEmail">Custom Email Domain</Label>
                  <Input
                    id="customEmail"
                    value={whitelabelSettings.custom_email_domain || ''}
                    onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, custom_email_domain: e.target.value }))}
                    placeholder="notifications@yourcompany.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primaryColor"
                      type="color"
                      value={whitelabelSettings.primary_color || '#3b82f6'}
                      onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-16"
                    />
                    <Input
                      value={whitelabelSettings.primary_color || '#3b82f6'}
                      onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryColor">Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="secondaryColor"
                      type="color"
                      value={whitelabelSettings.secondary_color || '#10b981'}
                      onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="w-16"
                    />
                    <Input
                      value={whitelabelSettings.secondary_color || '#10b981'}
                      onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accentColor">Accent Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="accentColor"
                      type="color"
                      value={whitelabelSettings.accent_color || '#8b5cf6'}
                      onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                      className="w-16"
                    />
                    <Input
                      value={whitelabelSettings.accent_color || '#8b5cf6'}
                      onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logoUrl">Logo URL</Label>
                <Input
                  id="logoUrl"
                  value={whitelabelSettings.logo_url || ''}
                  onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="faviconUrl">Favicon URL</Label>
                <Input
                  id="faviconUrl"
                  value={whitelabelSettings.favicon_url || ''}
                  onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, favicon_url: e.target.value }))}
                  placeholder="https://example.com/favicon.ico"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customCSS">Custom CSS</Label>
                <Textarea
                  id="customCSS"
                  value={whitelabelSettings.custom_css || ''}
                  onChange={(e) => setWhitelabelSettings(prev => ({ ...prev, custom_css: e.target.value }))}
                  placeholder="/* Add your custom CSS here */"
                  className="font-mono text-sm"
                  rows={6}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label htmlFor="hideBranding">Hide Internal Branding</Label>
                  <p className="text-sm text-muted-foreground">
                    Remove all references to the internal platform
                  </p>
                </div>
                <Switch
                  id="hideBranding"
                  checked={whitelabelSettings.hide_internal_branding || false}
                  onCheckedChange={(checked) => 
                    setWhitelabelSettings(prev => ({ ...prev, hide_internal_branding: checked }))
                  }
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleSaveWhitelabel}
                  disabled={saveWhitelabelMutation.isPending}
                >
                  {saveWhitelabelMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
                {!whitelabelSettings.is_active && (
                  <Button 
                    onClick={handleActivate}
                    disabled={activateWhitelabelMutation.isPending}
                    variant="default"
                  >
                    {activateWhitelabelMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Activating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Activate Whitelabel
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle>Brand Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="p-6 border rounded-lg"
                style={{ 
                  backgroundColor: `${whitelabelSettings.primary_color || '#3b82f6'}10`,
                  borderColor: `${whitelabelSettings.primary_color || '#3b82f6'}30`
                }}
              >
                <div className="text-center space-y-4">
                  <div 
                    className="text-2xl font-bold"
                    style={{ color: whitelabelSettings.primary_color || '#3b82f6' }}
                  >
                    {whitelabelSettings.brand_name || 'Your Brand Name'}
                  </div>
                  <div className="p-4 bg-background rounded border">
                    <div className="text-sm text-muted-foreground mb-2">Sample Dashboard Header</div>
                    <div 
                      className="h-8 rounded"
                      style={{ backgroundColor: whitelabelSettings.primary_color || '#3b82f6' }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Domains Tab */}
        <TabsContent value="domains" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Add Custom Domain</CardTitle>
              <CardDescription>
                Add a custom domain for your workspace. You'll need to configure DNS records to verify ownership.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="subdomain">Subdomain (optional)</Label>
                  <Input
                    id="subdomain"
                    placeholder="billing"
                    value={newSubdomain}
                    onChange={(e) => setNewSubdomain(e.target.value)}
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    placeholder="yourcompany.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleAddDomain}
                    disabled={addDomainMutation.isPending || !newDomain.trim()}
                  >
                    {addDomainMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Domain
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Domain List */}
          <div className="space-y-4">
            {loadingDomains ? (
              <div className="text-center py-8 text-muted-foreground">Loading domains...</div>
            ) : domains.length > 0 ? (
              domains.map((domain) => (
                <Card key={domain.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{domain.full_domain}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant={getStatusColor(domain.status)}>
                          {domain.status}
                        </Badge>
                        <Badge variant={getStatusColor(domain.ssl_status)}>
                          SSL: {domain.ssl_status}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {domain.dns_records && domain.dns_records.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">DNS Records</h4>
                          <div className="space-y-2">
                            {domain.dns_records.map((record, index) => (
                              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{record.type}</Badge>
                                    <span className="font-mono text-sm">{record.name}</span>
                                  </div>
                                  <p className="font-mono text-sm text-muted-foreground mt-1">
                                    {record.value}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(record.value)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No custom domains configured. Add one above to get started.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Domain Mappings Tab */}
        <TabsContent value="mappings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Domain-Level Feature Mapping</CardTitle>
              <CardDescription>
                Configure which features are available for specific domains. When domain-level mapping is enabled,
                these settings override workspace-level configurations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMappings ? (
                <div className="text-center py-8 text-muted-foreground">Loading mappings...</div>
              ) : mappings.length > 0 ? (
                <div className="space-y-4">
                  {mappings.map((mapping) => (
                    <div key={mapping.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-semibold">{mapping.domain_pattern}</div>
                          <div className="text-sm text-muted-foreground">
                            Type: {mapping.mapping_type} • Priority: {mapping.priority}
                          </div>
                        </div>
                        <Badge variant={mapping.is_active ? 'default' : 'secondary'}>
                          {mapping.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {mapping.enabled_features && mapping.enabled_features.length > 0 && (
                        <div className="mt-2">
                          <span className="text-sm font-medium">Enabled Features: </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {mapping.enabled_features.map((feature) => (
                              <Badge key={feature} variant="outline" className="text-xs">
                                {feature}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No domain mappings configured. Domain-level mapping is disabled when no mappings exist.
                </div>
              )}
            </CardContent>
          </Card>

          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Domain-Level Mapping:</strong> When enabled, domain-level mappings take precedence over
              workspace-level settings. This allows you to customize features per domain while maintaining
              workspace-level whitelabel branding.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Subdomain Request Card Component
interface SubdomainRequestCardProps {
  request: SubdomainRequest;
  onApprove: () => void;
  onReject: () => void;
}

function SubdomainRequestCard({ request, onApprove, onReject }: SubdomainRequestCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const approveMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call the approval function
      const { data, error } = await supabase.rpc('approve_subdomain_request', {
        p_request_id: request.id,
        p_reviewed_by: user.id,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subdomain-requests'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-custom-domains'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-whitelabel'] });
      toast({
        title: 'Request approved',
        description: 'The subdomain request has been approved and configured.',
      });
      onApprove();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error approving request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (reason: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('subdomain_requests')
        .update({
          status: 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason,
        })
        .eq('id', request.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subdomain-requests'] });
      toast({
        title: 'Request rejected',
        description: 'The subdomain request has been rejected.',
      });
      setShowRejectDialog(false);
      setRejectionReason('');
      onReject();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error rejecting request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'pending': case 'under_review': return 'secondary';
      case 'rejected': return 'destructive';
      default: return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{request.full_requested_domain}</CardTitle>
            <CardDescription>
              Requested by user • {new Date(request.created_at).toLocaleDateString()}
            </CardDescription>
          </div>
          <Badge variant={getStatusColor(request.status)}>
            {request.status.replace('_', ' ')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {request.requested_brand_name && (
          <div>
            <Label className="text-sm font-medium">Brand Name</Label>
            <p>{request.requested_brand_name}</p>
          </div>
        )}

        {(request.requested_primary_color || request.requested_secondary_color || request.requested_accent_color) && (
          <div>
            <Label className="text-sm font-medium mb-2 block">Brand Colors</Label>
            <div className="flex gap-2">
              {request.requested_primary_color && (
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: request.requested_primary_color }}
                  />
                  <span className="text-sm font-mono">{request.requested_primary_color}</span>
                </div>
              )}
              {request.requested_secondary_color && (
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: request.requested_secondary_color }}
                  />
                  <span className="text-sm font-mono">{request.requested_secondary_color}</span>
                </div>
              )}
              {request.requested_accent_color && (
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: request.requested_accent_color }}
                  />
                  <span className="text-sm font-mono">{request.requested_accent_color}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {request.additional_notes && (
          <div>
            <Label className="text-sm font-medium">Additional Notes</Label>
            <p className="text-sm text-muted-foreground">{request.additional_notes}</p>
          </div>
        )}

        {request.rejection_reason && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Rejection Reason:</strong> {request.rejection_reason}
            </AlertDescription>
          </Alert>
        )}

        {request.status === 'pending' || request.status === 'under_review' ? (
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
              className="flex-1"
            >
              {approveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowRejectDialog(true)}
              disabled={rejectMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </Button>
          </div>
        ) : null}

        {showRejectDialog && (
          <Card className="mt-4 border-destructive">
            <CardHeader>
              <CardTitle>Reject Request</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="rejectionReason">Rejection Reason</Label>
                <Textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={() => rejectMutation.mutate(rejectionReason)}
                  disabled={rejectMutation.isPending || !rejectionReason.trim()}
                  className="flex-1"
                >
                  {rejectMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    'Confirm Rejection'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectionReason('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}

